import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser($headline: String, $show_real_name: Boolean) {
    updateCurrentUser(headline: $headline, show_real_name: $show_real_name)
  }
`;

const GENERATE_HEADLINE_MUTATION = gql`
  mutation GenerateHeadline {
    generateAiHeadline
  }
`;

const ProfileHeaderEditModal = ({ user, onClose, onSave, onError }) => {
  const intl = useIntl();
  const [headline, setHeadline] = useState(user.headline || '');
  const [showRealName, setShowRealName] = useState(user.show_real_name || false);
  const [isGeneratingHeadline, setIsGeneratingHeadline] = useState(false);

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => onSave('Profile updated successfully'),
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const [generateHeadline] = useMutation(GENERATE_HEADLINE_MUTATION, {
    onCompleted: (data) => {
      setHeadline(data.generateAiHeadline);
      setIsGeneratingHeadline(false);
    },
    onError: (error) => {
      console.error('Error generating headline:', error);
      setIsGeneratingHeadline(false);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateUser({ variables: { headline, show_real_name: showRealName } });
  };

  const handleGenerateHeadline = async () => {
    setIsGeneratingHeadline(true);
    try {
      await generateHeadline();
    } catch (error) {
      console.error('Error generating headline:', error);
      setIsGeneratingHeadline(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg p-6 max-w-2xl w-full mx-4 border border-succession-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.title' })}</h2>
            <p className="text-succession-text-muted text-base mt-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.subtitle' })}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Headline Field */}
          <div>
            <label htmlFor="headline" className="block text-sm font-medium text-succession-text-primary mb-2">
              {intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.headline_label' })}
            </label>
            <textarea
              id="headline"
              rows="3"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary"
              placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.headline_placeholder' })}
            />
            <p className="mt-1 text-xs text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.headline_hint' })}
            </p>
          </div>

          {/* AI Generation Button - only show if ai_headlines feature is enabled */}
          {user.company?.feature_types?.includes('ai_headlines') && (
            <div>
              <button
                type="button"
                onClick={handleGenerateHeadline}
                disabled={isGeneratingHeadline}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors ${
                  isGeneratingHeadline
                    ? 'text-succession-text-muted bg-succession-bg-secondary cursor-not-allowed'
                    : 'text-white bg-succession-primary hover:opacity-80'
                }`}
              >
                {isGeneratingHeadline ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {intl.formatMessage({ id: 'succession.profile_edit.common.generating' })}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.profile_edit.common.generate_with_ai' })}
                  </>
                )}
              </button>
            </div>
          )}

          {user.motivation === 'growth' && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
              <input
                type="checkbox"
                id="showRealName"
                checked={showRealName}
                onChange={(e) => setShowRealName(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-succession-border text-succession-primary focus:ring-succession-primary"
              />
              <label htmlFor="showRealName" className="text-sm text-succession-text-primary cursor-pointer">
                {intl.formatMessage({ id: 'succession.profile_edit.modals.profile_header_edit.show_real_name' })}
              </label>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-succession-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                loading
                  ? 'bg-succession-primary opacity-50 cursor-not-allowed'
                  : 'bg-succession-primary hover:opacity-80'
              }`}
            >
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ProfileHeaderEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default ProfileHeaderEditModal;
